/**
 * Shared reader for the Server-Sent Event streams the interview endpoints emit.
 *
 * Both mock-interview modules stream from FastAPI in the same wire format —
 * `data: {"type": "text_chunk" | "stream_end" | "error", ...}\n\n` — and both
 * had independently hand-rolled the read loop, in four places total. Every copy
 * carried the same defect: the decoded chunk was split on newlines with no
 * buffer carried across `reader.read()`, so a frame split by a network read was
 * silently lost (its head failed `JSON.parse`, its tail failed the `data: `
 * test). Words vanished mid-answer, and a split `stream_end` frame made a
 * complete response look like a dropped connection.
 *
 * Deliberately a plain async function rather than a hook: it holds no React
 * state, so the hooks-based behavioral module and the monolithic technical
 * module can share it without either having to change shape first.
 */

/**
 * Read an SSE response to completion, dispatching each parsed frame.
 *
 * @param {Response} response - fetch Response whose body is an SSE stream
 * @param {(data: object) => boolean|void} onEvent - called per parsed frame.
 *   Return `false` to stop reading (e.g. on `stream_end`); any other value
 *   continues. Throwing propagates to the caller.
 * @returns {Promise<{completed: boolean}>} `completed` is true when the caller
 *   stopped the stream by returning false — i.e. a terminal frame was seen —
 *   and false when the stream simply ran out (dropped connection, abort).
 */
export async function readSSEStream(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completed = false;

  /**
   * Dispatch every complete line in `lines`.
   * @returns {boolean} false if the consumer asked to stop.
   */
  const dispatchLines = (lines) => {
    for (const line of lines) {
      // The space after `data:` is optional per the SSE spec, so match on the
      // colon and strip at most one leading space. `\r` is tolerated because a
      // CRLF-terminated stream leaves it on the end of every line.
      if (!line.startsWith('data:')) continue;

      const dataStr = line.slice(5).replace(/^ /, '').trim();
      if (dataStr === '') continue;

      let data;
      try {
        data = JSON.parse(dataStr);
      } catch (parseError) {
        // A frame that is well-formed but not valid JSON is a server bug, not
        // a framing problem — log and keep reading rather than killing the turn.
        console.error('Error parsing SSE data:', parseError);
        continue;
      }

      if (onEvent(data) === false) return false;
    }
    return true;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // `decoder.decode(..., {stream: true})` only protects multi-byte UTF-8
      // code points across reads — it knows nothing about frame boundaries.
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      // Retain the trailing fragment: it is either an empty string (the read
      // ended on a frame boundary) or the start of a frame still in flight.
      buffer = lines.pop() ?? '';

      if (!dispatchLines(lines)) {
        completed = true;
        return { completed };
      }
    }

    // Flush whatever the buffer still holds. A stream whose last frame has no
    // trailing newline would otherwise leave that frame stranded — and when the
    // stranded frame is stream_end, the caller concludes the connection dropped:
    // the interview never completes and feedback never runs.
    buffer += decoder.decode();
    if (buffer.trim() !== '' && !dispatchLines(buffer.split('\n'))) {
      completed = true;
    }
  } finally {
    reader.releaseLock();
  }

  return { completed };
}

export default readSSEStream;
